import { blurRgba, blurScratchSize, fogBlurPlan } from "daedalus-core";
import { FOG_CELL_TILES, isExplored, resolveColour, type ExploredSet, type Size } from "odyssey-shared";
import { MAP_SCALE } from "./geometry.ts";

/**
 * The fade over unexplored ground (ADR-100 decisions 2 and 3, the amendment's decision 26): the whole map is drawn, and
 * each fog cell the character has not explored is washed over, the ground desaturated and darkened toward cool ink,
 * every other tile a shade darker, and the tiles along a side that meets explored ground darker still. The fade is a
 * look over ground that is drawn, never a lock. A cell is composed from the ground picture onto the display picture:
 * its ground, then the wash if it is unexplored. Nothing here knows the game: a context, a ground source and a set
 * come in, and calls go out.
 */

/**
 * The colours of the fade and of ground not yet drawn: the card's wash (cool ink at 55 percent), a check one shade
 * darker, the 30 percent edge, the grey the desaturation blends toward, and the placeholder's ink and dot.
 */
export const FOG_COLOURS = Object.freeze({
  ink: resolveColour("ink-900"),
  dot: resolveColour("ink-700"),
  wash: "rgba(24,28,34,0.55)",
  check: "rgba(7,8,11,0.1)",
  edge: "rgba(7,8,11,0.3)",
  grey: "#808080",
});

/**
 * How much of the colour the desaturation takes away, the card's .85.
 */
export const FOG_DESATURATION = 0.85;

/**
 * The part of a 2D context the fade uses; `S` is what the ground is drawn from.
 */
export interface FogContext<S> {
  fillStyle: string | CanvasGradient | CanvasPattern;
  globalAlpha: number;
  globalCompositeOperation: string;
  fillRect(x: number, y: number, width: number, height: number): void;
  drawImage(source: S, sx: number, sy: number, sw: number, sh: number, dx: number, dy: number, dw: number, dh: number): void;
}

/**
 * What the blur of an unexplored cell needs (ADR-100 decisions 62 and 63): a way to read ground pixels, a way to put
 * finished pixels on the display, and the arrays the blur works in, kept between cells so nothing is allocated a cell.
 */
export interface FogBlur {
  readonly read: (x: number, y: number, width: number, height: number) => ArrayLike<number> | null;
  readonly put: (pixels: Uint8ClampedArray, x: number, y: number, width: number, height: number) => void;
  source: Uint8ClampedArray;
  output: Uint8ClampedArray;
  scratch: Float64Array;
}

/**
 * Makes the blur of a cell from a reader of the ground and a writer to the display.
 *
 * @param read Reads a rectangle of the ground as straight RGBA, or null when it cannot be read.
 * @param put Writes a rectangle of opaque RGBA pixels to the display, replacing what is there.
 * @returns The blur, with empty working arrays that grow to the largest cell read.
 */
export function createFogBlur(read: FogBlur["read"], put: FogBlur["put"]): FogBlur {
  return { read, put, source: new Uint8ClampedArray(0), output: new Uint8ClampedArray(0), scratch: new Float64Array(0) };
}

/**
 * A fog cell as `[column, row]`.
 */
export type FogCell = readonly [number, number];

/**
 * Lays the placeholder of ground not yet drawn over the whole picture: the ground's mean tone alone when one is given
 * (P2.12-T32), else ink with a dot at every fourth tile.
 *
 * @param context The display context.
 * @param width The picture's width in pixels.
 * @param height The picture's height in pixels.
 * @param scale Pixels a tile.
 * @param tone The ground's mean tone as a CSS colour, or omitted before any ground is drawn.
 */
export function paintPlaceholder<S>(context: FogContext<S>, width: number, height: number, scale: number = MAP_SCALE, tone?: string): void {
  context.globalAlpha = 1;
  context.globalCompositeOperation = "source-over";
  if (tone !== undefined) {
    context.fillStyle = tone;
    context.fillRect(0, 0, width, height);
    return;
  }
  context.fillStyle = FOG_COLOURS.ink;
  context.fillRect(0, 0, width, height);
  context.fillStyle = FOG_COLOURS.dot;
  const step = 4 * scale;
  for (let y = 0; y < height; y += step) {
    for (let x = 0; x < width; x += step) {
      context.fillRect(x, y, scale, scale);
    }
  }
}

const INK_RED = Number.parseInt(FOG_COLOURS.ink.slice(1, 3), 16);
const INK_GREEN = Number.parseInt(FOG_COLOURS.ink.slice(3, 5), 16);
const INK_BLUE = Number.parseInt(FOG_COLOURS.ink.slice(5, 7), 16);
const OPAQUE = 255;

/**
 * Blurs an unexplored cell's ground and puts the cell's own pixels on the display over the ink. The blur reads the
 * ground with a margin and never the display.
 *
 * @param blur The reader, writer and working arrays.
 * @param x The cell's left in pixels.
 * @param y The cell's top in pixels.
 * @param width The cell's width, cut to the map.
 * @param height The cell's height, cut to the map.
 * @param scale Pixels a tile.
 * @param map The map's size in tiles.
 * @returns False when the ground could not be read, leaving the display as it was.
 */
function putBlurred(blur: FogBlur, x: number, y: number, width: number, height: number, scale: number, map: Size): boolean {
  const plan = fogBlurPlan({ x, y, width, height }, scale, { width: map.width * scale, height: map.height * scale });
  const { read, write, offset, radius } = plan;
  const pixels = blur.read(read.x, read.y, read.width, read.height);
  const bytes = read.width * read.height * 4;
  if (pixels === null || pixels.length !== bytes) {
    return false;
  }
  if (blur.source.length < bytes) {
    blur.source = new Uint8ClampedArray(bytes);
    blur.output = new Uint8ClampedArray(bytes);
  }
  if (blur.scratch.length < blurScratchSize(read.width, read.height)) {
    blur.scratch = new Float64Array(blurScratchSize(read.width, read.height));
  }
  const source = blur.source.subarray(0, bytes);
  const output = blur.output.subarray(0, bytes);
  source.set(pixels as ArrayLike<number>);
  blurRgba(source, read.width, read.height, radius, output, blur.scratch);
  const own = new Uint8ClampedArray(write.width * write.height * 4);
  for (let row = 0; row < write.height; row += 1) {
    for (let column = 0; column < write.width; column += 1) {
      const from = ((row + offset.y) * read.width + column + offset.x) * 4;
      const to = (row * write.width + column) * 4;
      const alpha = output[from + 3] as number;
      own[to] = Math.round(((output[from] as number) * alpha + INK_RED * (OPAQUE - alpha)) / OPAQUE);
      own[to + 1] = Math.round(((output[from + 1] as number) * alpha + INK_GREEN * (OPAQUE - alpha)) / OPAQUE);
      own[to + 2] = Math.round(((output[from + 2] as number) * alpha + INK_BLUE * (OPAQUE - alpha)) / OPAQUE);
      own[to + 3] = OPAQUE;
    }
  }
  blur.put(own, write.x, write.y, write.width, write.height);
  return true;
}

/**
 * Composes one fog cell onto the display: ink under it, the ground's pixels of the cell, and, when the cell is not
 * explored, the wash. An unexplored cell's ground is blurred when a blur is given and the ground can be read; an
 * explored cell is always sharp. A cell off the set draws nothing; a short last cell is cut to the map.
 *
 * @param context The display context.
 * @param ground What the ground picture is drawn from.
 * @param set The character's exploration.
 * @param column The cell's column.
 * @param row The cell's row.
 * @param map The map's size in tiles.
 * @param scale Pixels a tile.
 * @param blur The blur of unexplored ground, or omitted to draw it sharp.
 */
export function paintFogCell<S>(context: FogContext<S>, ground: S, set: ExploredSet, column: number, row: number, map: Size, scale: number = MAP_SCALE, blur?: FogBlur): void {
  if (column < 0 || row < 0 || column >= set.columns || row >= set.rows) {
    return;
  }
  const side = FOG_CELL_TILES * scale;
  const x = column * side;
  const y = row * side;
  const width = Math.min(side, map.width * scale - x);
  const height = Math.min(side, map.height * scale - y);
  if (width <= 0 || height <= 0) {
    return;
  }
  context.globalAlpha = 1;
  context.globalCompositeOperation = "source-over";
  context.fillStyle = FOG_COLOURS.ink;
  context.fillRect(x, y, width, height);
  const explored = isExplored(set, column, row);
  if (explored || blur === undefined || !putBlurred(blur, x, y, width, height, scale, map)) {
    context.drawImage(ground, x, y, width, height, x, y, width, height);
  }
  if (explored) {
    return;
  }
  context.globalCompositeOperation = "saturation";
  context.globalAlpha = FOG_DESATURATION;
  context.fillStyle = FOG_COLOURS.grey;
  context.fillRect(x, y, width, height);
  context.globalCompositeOperation = "source-over";
  context.globalAlpha = 1;
  context.fillStyle = FOG_COLOURS.wash;
  context.fillRect(x, y, width, height);
  context.fillStyle = FOG_COLOURS.check;
  for (let tileY = 0; tileY < FOG_CELL_TILES; tileY += 1) {
    for (let tileX = 0; tileX < FOG_CELL_TILES; tileX += 1) {
      const left = x + tileX * scale;
      const top = y + tileY * scale;
      if ((tileX + tileY) % 2 === 1 && left < x + width && top < y + height) {
        context.fillRect(left, top, scale, scale);
      }
    }
  }
  context.fillStyle = FOG_COLOURS.edge;
  if (isExplored(set, column, row - 1)) {
    context.fillRect(x, y, width, scale);
  }
  if (isExplored(set, column - 1, row)) {
    context.fillRect(x, y, scale, height);
  }
  if (isExplored(set, column, row + 1)) {
    context.fillRect(x, y + height - scale, width, scale);
  }
  if (isExplored(set, column + 1, row)) {
    context.fillRect(x + width - scale, y, scale, height);
  }
}

/**
 * The cells whose state differs between two sets: every cell of the second when the first is absent or has other
 * counts, else each cell set in the second and not in the first.
 *
 * @param before The set held, or null for none.
 * @param after The set now.
 * @returns The cells, row-major.
 */
export function changedCells(before: ExploredSet | null, after: ExploredSet): readonly FogCell[] {
  const cells: FogCell[] = [];
  const same = before !== null && before.columns === after.columns && before.rows === after.rows;
  for (let row = 0; row < after.rows; row += 1) {
    for (let column = 0; column < after.columns; column += 1) {
      if (!same || (isExplored(after, column, row) !== isExplored(before as ExploredSet, column, row))) {
        cells.push([column, row]);
      }
    }
  }
  return cells;
}

/**
 * The cells to compose again when some cells clear: each, and the four beside it, whose edge tiles change with it
 * (the amendment's decision 26). Each is listed once, kept on the map.
 *
 * @param cleared The cells that changed.
 * @param columns The map's cells across.
 * @param rows The map's cells down.
 * @returns The cells, each cell followed by the new neighbours it brings.
 */
export function cellsToRedraw(cleared: readonly FogCell[], columns: number, rows: number): readonly FogCell[] {
  const seen = new Set<number>();
  const cells: FogCell[] = [];
  const add = (column: number, row: number): void => {
    if (column < 0 || row < 0 || column >= columns || row >= rows || seen.has(row * columns + column)) {
      return;
    }
    seen.add(row * columns + column);
    cells.push([column, row]);
  };
  for (const [column, row] of cleared) {
    add(column, row);
    add(column + 1, row);
    add(column, row + 1);
    add(column - 1, row);
    add(column, row - 1);
  }
  return cells;
}
